/* ==========================================================================
   🔥HA Cart Drawer
   Single custom element + event delegation. Every cart mutation goes through
   one serialized queue and one render path.
   ========================================================================== */

(function () {
  'use strict';

  var OPT_OUT_KEY = 'ha_cart_optout_v1';
  var TIMER_KEY = 'ha_cart_timer_deadline';

  /* ---------- opt-out memory ---------- */

  function readOptOut() {
    try {
      var raw = window.localStorage.getItem(OPT_OUT_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeOptOut(list) {
    try {
      window.localStorage.setItem(OPT_OUT_KEY, JSON.stringify(list));
    } catch (e) {
      /* private mode, blocked storage — degrade to session-only behaviour */
    }
  }

  function isOptedOut(handle) {
    return readOptOut().indexOf(handle) !== -1;
  }

  function setOptOut(handle, value) {
    var list = readOptOut();
    var i = list.indexOf(handle);
    if (value && i === -1) list.push(handle);
    if (!value && i !== -1) list.splice(i, 1);
    writeOptOut(list);
  }

  function clearOptOut() {
    writeOptOut([]);
  }

  /* ---------- serialized request queue ---------- */

  var queue = Promise.resolve();

  function enqueue(task) {
    queue = queue.then(task, task).catch(function (err) {
      console.error('[ha-cart-drawer]', err);
    });
    return queue;
  }

  /* ---------- helpers ---------- */

  function money(value) {
    return value;
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  /* ---------- the element ---------- */

  class HACartDrawer extends HTMLElement {
    constructor() {
      super();
      this._busy = false;
      this._timerId = null;
      this._onKeydown = this._onKeydown.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onChange = this._onChange.bind(this);
      this._onInput = debounce(this._commitQtyInput.bind(this), 450);
    }

    connectedCallback() {
      this.addEventListener('click', this._onClick);
      this.addEventListener('change', this._onChange);
      this.addEventListener('input', this._onQtyTyping.bind(this));
      document.addEventListener('keydown', this._onKeydown);
      this._bindOpeners();
      this._startTimer();
      this.sync();
      window.HACartDrawer = this;
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeydown);
      if (this._timerId) clearInterval(this._timerId);
    }

    /* ---------- open / close ---------- */

    _bindOpeners() {
      var selector = this.dataset.openSelector;
      if (!selector) return;
      var self = this;
      document.addEventListener(
        'click',
        function (event) {
          var trigger = event.target.closest(selector);
          if (!trigger) return;
          if (self.contains(trigger)) return;
          event.preventDefault();
          self.open(trigger);
        },
        false
      );
    }

    open(trigger) {
      if (trigger) this._returnFocus = trigger;
      this.classList.add('is-active');
      document.body.classList.add('ha-cart-open');
      var inner = this.querySelector('.ha-drawer__inner');
      if (inner) {
        window.setTimeout(function () {
          inner.focus({ preventScroll: true });
        }, 50);
      }
      this._startTimer();
      document.dispatchEvent(new CustomEvent('ha:cart:open'));
    }

    close() {
      this.classList.remove('is-active');
      document.body.classList.remove('ha-cart-open');
      if (this._returnFocus && typeof this._returnFocus.focus === 'function') {
        this._returnFocus.focus({ preventScroll: true });
      }
      document.dispatchEvent(new CustomEvent('ha:cart:close'));
    }

    _onKeydown(event) {
      if (event.key !== 'Escape') return;
      if (!this.classList.contains('is-active')) return;
      this.close();
    }

    /* ---------- delegated events ---------- */

    _onClick(event) {
      var closeEl = event.target.closest('[data-ha-close]');
      if (closeEl) {
        event.preventDefault();
        this.close();
        return;
      }

      var toggle = event.target.closest('[data-ha-toggle]');
      if (toggle) {
        event.preventDefault();
        this._handleToggle(toggle);
        return;
      }

      var remove = event.target.closest('[data-ha-remove]');
      if (remove) {
        event.preventDefault();
        this._setLineLoading(remove, true);
        this.changeLine(remove.dataset.key, 0);
        return;
      }

      var minus = event.target.closest('[data-ha-qty-minus]');
      if (minus) {
        event.preventDefault();
        this._step(minus, -1);
        return;
      }

      var plus = event.target.closest('[data-ha-qty-plus]');
      if (plus) {
        event.preventDefault();
        this._step(plus, 1);
        return;
      }

      var checkout = event.target.closest('[data-ha-checkout]');
      if (checkout) {
        checkout.classList.add('is-loading');
        window.setTimeout(function () {
          checkout.classList.remove('is-loading');
        }, 8000);
      }
    }

    _onChange(event) {
      var input = event.target.closest('[data-ha-qty-input]');
      if (!input) return;
      this._commitQtyInput(input);
    }

    _onQtyTyping(event) {
      var input = event.target.closest('[data-ha-qty-input]');
      if (!input) return;
      this._onInput(input);
    }

    _step(button, delta) {
      var wrap = button.closest('[data-ha-qty]');
      if (!wrap) return;
      var input = wrap.querySelector('[data-ha-qty-input]');
      if (!input) return;
      var next = parseInt(input.value, 10);
      if (isNaN(next)) next = parseInt(input.dataset.initial, 10) || 1;
      next = next + delta;
      if (next < 0) next = 0;
      input.value = next;
      this._setLineLoading(input, true);
      this.changeLine(input.dataset.key, next);
    }

    _commitQtyInput(input) {
      if (!input || !input.dataset) return;
      var next = parseInt(input.value, 10);
      var initial = parseInt(input.dataset.initial, 10);
      if (isNaN(next) || next < 0) {
        input.value = isNaN(initial) ? 1 : initial;
        return;
      }
      if (next === initial) return;
      this._setLineLoading(input, true);
      this.changeLine(input.dataset.key, next);
    }

    _setLineLoading(node, state) {
      var item = node.closest('.ha-item');
      if (item) item.classList.toggle('is-loading', !!state);
    }

    /* ---------- upsell toggle ---------- */

    _handleToggle(button) {
      var wrap = button.closest('[data-ha-upsell]');
      if (!wrap) return;
      if (button.hasAttribute('disabled')) return;

      var handle = wrap.dataset.handle;
      var turningOn = wrap.dataset.selected !== 'true';

      setOptOut(handle, !turningOn);
      this._paintToggle(wrap, turningOn);
      button.setAttribute('disabled', '');

      if (turningOn) {
        this.addVariant(wrap.dataset.variantId, 1);
      } else if (wrap.dataset.key) {
        this.changeLine(wrap.dataset.key, 0);
      }
    }

    _paintToggle(wrap, on) {
      wrap.dataset.selected = on ? 'true' : 'false';
      var button = wrap.querySelector('[data-ha-toggle]');
      if (!button) return;
      button.classList.toggle('is-on', !!on);
      button.setAttribute('aria-checked', on ? 'true' : 'false');
    }

    /* ---------- reconcile, once per render ---------- */

    sync() {
      if (this._busy) return;

      var orphan = this.dataset.orphanKey;
      if (orphan) {
        this._busy = true;
        var self = this;
        this.changeLine(orphan, 0).then(function () {
          self._busy = false;
        });
        return;
      }

      if (this.classList.contains('ha-drawer--empty')) {
        clearOptOut();
        return;
      }

      var wrap = this.querySelector('[data-ha-upsell]');
      if (!wrap) return;

      var handle = wrap.dataset.handle;
      var inCart = wrap.dataset.inCart === 'true';
      var qty = parseInt(wrap.dataset.inCartQty, 10) || 0;
      var defaultOn = wrap.dataset.defaultOn === 'true';
      var want = isOptedOut(handle) ? false : inCart ? true : defaultOn;

      this._paintToggle(wrap, want);

      var self2 = this;
      var done = function () {
        self2._busy = false;
      };

      if (want && !inCart) {
        this._busy = true;
        this.addVariant(wrap.dataset.variantId, 1).then(done);
        return;
      }

      if (!want && inCart) {
        this._busy = true;
        this.changeLine(wrap.dataset.key, 0).then(done);
        return;
      }

      if (want && inCart && qty > 1) {
        this._busy = true;
        this.changeLine(wrap.dataset.key, 1).then(done);
      }
    }

    /* ---------- cart API ---------- */

    _sectionList() {
      return [this.dataset.sectionId, this.dataset.bubbleSection]
        .filter(Boolean)
        .join(',');
    }

    _post(url, payload) {
      var body = Object.assign(
        {
          sections: this._sectionList(),
          sections_url: window.location.pathname
        },
        payload
      );

      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(body)
      }).then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok && !data.sections) {
            var message = data.description || data.message || 'Cart request failed';
            throw new Error(message);
          }
          return data;
        });
      });
    }

    changeLine(key, quantity) {
      var self = this;
      return enqueue(function () {
        return self
          ._post('/cart/change.js', { id: key, quantity: quantity })
          .then(function (data) {
            self.render(data);
          })
          .catch(function (err) {
            self._showError(err.message);
            self.refresh();
          });
      });
    }

    addVariant(variantId, quantity) {
      var self = this;
      return enqueue(function () {
        return self
          ._post('/cart/add.js', {
            items: [{ id: Number(variantId), quantity: quantity || 1 }]
          })
          .then(function (data) {
            self.render(data);
          })
          .catch(function (err) {
            self._showError(err.message);
            self.refresh();
          });
      });
    }

    addFromForm(form) {
      var self = this;
      var formData = new FormData(form);
      formData.append('sections', this._sectionList());
      formData.append('sections_url', window.location.pathname);

      return enqueue(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        })
          .then(function (r) {
            return r.json();
          })
          .then(function (data) {
            if (data.status && Number(data.status) !== 200) {
              throw new Error(data.description || 'Could not add to cart');
            }
            self.render(data);
            if (self.dataset.openOnAdd === 'true') self.open();
          })
          .catch(function (err) {
            self._showError(err.message);
          });
      });
    }

    refresh() {
      var self = this;
      return enqueue(function () {
        return self
          ._post('/cart/update.js', { updates: {} })
          .then(function (data) {
            self.render(data);
          })
          .catch(function () {});
      });
    }

    /* ---------- render ---------- */

    render(data) {
      if (!data || !data.sections) return;

      var sectionId = this.dataset.sectionId;
      var html = data.sections[sectionId];

      if (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('ha-cart-drawer');

        if (fresh) {
          var body = this.querySelector('.ha-drawer__body');
          var scroll = body ? body.scrollTop : 0;
          var wasActive = this.classList.contains('is-active');

          // replace contents
          this.innerHTML = fresh.innerHTML;

          // sync attributes from the fresh render onto the persistent root
          var keep = ['id'];
          var i;
          var liveAttrs = Array.prototype.map.call(this.attributes, function (a) {
            return a.name;
          });

          for (i = 0; i < liveAttrs.length; i++) {
            if (keep.indexOf(liveAttrs[i]) !== -1) continue;
            if (!fresh.hasAttribute(liveAttrs[i])) this.removeAttribute(liveAttrs[i]);
          }

          for (i = 0; i < fresh.attributes.length; i++) {
            if (keep.indexOf(fresh.attributes[i].name) !== -1) continue;
            this.setAttribute(fresh.attributes[i].name, fresh.attributes[i].value);
          }

          // the class attribute we just copied carries no open state
          if (wasActive) this.classList.add('is-active');

          var freshBody = this.querySelector('.ha-drawer__body');
          if (freshBody) freshBody.scrollTop = scroll;

          this._paintError();
          this._startTimer();
        }
      }

      this._renderBubble(data);

      document.dispatchEvent(new CustomEvent('ha:cart:updated', { detail: data }));

      this.sync();
    }

    _renderBubble(data) {
      var bubbleId = this.dataset.bubbleSection;
      if (!bubbleId) return;

      var markup = data.sections[bubbleId];
      if (!markup) return;

      var target =
        document.getElementById('cart-icon-bubble') ||
        document.querySelector('[data-ha-cart-bubble]') ||
        document.querySelector('.header__icon--cart');
      if (!target) return;

      var doc = new DOMParser().parseFromString(markup, 'text/html');
      var source = doc.querySelector('.shopify-section') || doc.body;
      target.innerHTML = source.innerHTML;
    }

    /* ---------- errors ---------- */

    _showError(message) {
      this._error = message || '';
      this._paintError();
    }

    _paintError() {
      var message = this._error;
      if (!message) return;

      var status = this.querySelector('[data-ha-status]');
      if (status) status.textContent = message;

      var slots = this.querySelectorAll('.ha-item.is-loading [data-ha-item-error]');
      if (slots.length) {
        Array.prototype.forEach.call(slots, function (slot) {
          slot.textContent = message;
        });
      } else {
        var footer = this.querySelector('.ha-drawer__footer');
        var ctas = this.querySelector('.ha-ctas');
        if (footer) {
          var note = this.querySelector('[data-ha-error]');
          if (!note) {
            note = document.createElement('p');
            note.className = 'ha-item__error';
            note.setAttribute('data-ha-error', '');
            note.setAttribute('role', 'alert');
            footer.insertBefore(note, ctas || null);
          }
          note.textContent = message;
        }
      }

      this._error = '';
    }

    /* ---------- timer ---------- */

    _startTimer() {
      var self = this;

      if (this._timerId) {
        clearInterval(this._timerId);
        this._timerId = null;
      }

      var node = this.querySelector('[data-ha-timer]');
      if (!node) return;

      var clock = node.querySelector('[data-ha-timer-clock]');
      if (!clock) return;

      var minutes = parseInt(node.dataset.minutes, 10) || 10;
      var deadline = 0;

      try {
        deadline = parseInt(window.sessionStorage.getItem(TIMER_KEY), 10) || 0;
      } catch (e) {
        deadline = 0;
      }

      if (!deadline || deadline <= Date.now()) {
        deadline = Date.now() + minutes * 60 * 1000;
        try {
          window.sessionStorage.setItem(TIMER_KEY, String(deadline));
        } catch (e) {
          /* blocked storage — the timer restarts on every render */
        }
      }

      var tick = function () {
        var left = Math.max(0, Math.round((deadline - Date.now()) / 1000));
        var mins = Math.floor(left / 60);
        var secs = left % 60;
        clock.textContent = mins + ':' + (secs < 10 ? '0' + secs : secs);

        if (left > 0) return;

        if (self._timerId) {
          clearInterval(self._timerId);
          self._timerId = null;
        }
        try {
          window.sessionStorage.removeItem(TIMER_KEY);
        } catch (e) {}
      };

      tick();
      this._timerId = window.setInterval(tick, 1000);
    }
  }

  if (!window.customElements.get('ha-cart-drawer')) {
    window.customElements.define('ha-cart-drawer', HACartDrawer);
  }

  /* ---------- add-to-cart interception ---------- */

  document.addEventListener(
    'submit',
    function (event) {
      var drawer = document.querySelector('ha-cart-drawer');
      if (!drawer || drawer.dataset.intercept !== 'true') return;

      var form = event.target;
      if (!form || form.nodeName !== 'FORM') return;
      if (form.hasAttribute('data-ha-ignore')) return;
      if (drawer.contains(form)) return;

      var action = form.getAttribute('action') || '';
      if (action.indexOf('/cart/add') === -1) return;

      // capture phase, so the theme's own handler on the form never runs and
      // the product is not added twice
      event.preventDefault();
      event.stopImmediatePropagation();

      var button = form.querySelector('[type="submit"]');
      if (button) {
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
      }

      drawer.addFromForm(form).then(function () {
        if (!button) return;
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
      });
    },
    true
  );
})();
